// Painting a new picture for a library asset from the image maker. The picture joins the asset's versions straight
// away and becomes the look (the image maker picks what it just made). An asset with no picture yet (a new draft)
// is saved by it and moves into the library. It lands even if the page that asked for it has closed.
import type { PaintRef } from "../server/paint";
import type { FaceSpots } from "../model/face";
import type { Asset, LookVersion } from "../model/library";
import { findAsset } from "../model/library";
import { api } from "../ui/api";
import { addVersion, hasPicture, type Painted } from "./build";
import { latestAsset, saveNow, setDraft } from "./drafts";
import { getLibrary, putAsset } from "./store";

// Draw from words, or change the picture `from`.
// styleId: the drawing style; a character's first picture gives it that style's faces.
export type PaintRequest = { how: "drawn" | "changed"; text: string; refs: PaintRef[]; style?: string; styleId?: string; from?: { src: string; face?: FaceSpots } };

const NOUN: Record<Asset["kind"], string> = { character: "character", background: "place", prop: "prop" };

// The new version, and a note if a character's face is only a guess (the drawing didn't mark where it goes).
export async function paintLook(id: string, req: PaintRequest): Promise<{ version: LookVersion; note?: string }> {
  const a = latestAsset(id);
  if (!a) throw new Error("It was deleted.");
  const painted: Painted & { faceGuessed?: true } = req.how === "drawn"
    ? await api.paint(a.kind, req.text, req.refs, req.style)
    : await api.editAsset(a.kind, req.from!.src, req.text, req.from!.face, req.refs, req.style);
  const { faceGuessed, ...picture } = painted;
  const now = latestAsset(id);
  if (!now) throw new Error("It was deleted while drawing.");
  const first = !hasPicture(now);
  let next: Asset = addVersion(now, picture, req.how, req.text, true);
  if (first && req.how === "drawn") next = { ...next, description: req.text };
  if (first && now.kind === "character" && !now.faceStyle && req.styleId) next = { ...next, faceStyle: req.styleId };
  if (!next.name.trim()) next = { ...next, name: `Untitled ${NOUN[now.kind]}` };
  if (findAsset(getLibrary(), id)) putAsset(next); else setDraft(next);
  void saveNow(id);
  return { version: next.versions![next.versions!.length - 1]!, ...(faceGuessed ? { note: "Couldn't tell where the face goes, so it's a guess. Tap Change face to move it." } : {}) };
}
